/**
 * ChapterEditModal 컴포넌트
 *
 * 챕터 생성/편집 모달
 * - 제목, 순서 입력
 * - 생성/수정 모드 지원
 */

import React, { useState, useEffect } from 'react';

interface ChapterEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { title: string; seq?: number }) => Promise<void>;
  chapterId?: number; // 편집 모드일 때
  chapterTitle?: string; // 편집 모드일 때 기존 제목
  maxSeq?: number; // 현재 최대 순서 (생성 시 자동 계산용)
}

const ChapterEditModal: React.FC<ChapterEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  chapterId,
  chapterTitle,
  maxSeq = 0,
}) => {
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!chapterId;

  /**
   * 모달이 열릴 때 폼 초기화
   */
  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        // 편집 모드: 기존 데이터로 초기화
        setTitle(chapterTitle || '');
      } else {
        // 생성 모드: 빈 값으로 초기화
        setTitle('');
      }
      setError(null);
    }
  }, [isOpen, isEditMode, chapterTitle]);

  /**
   * 저장 핸들러
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 유효성 검사
    if (!title.trim()) {
      setError('챕터 제목을 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 생성 모드일 때만 seq를 자동 계산하여 전달
      const data: { title: string; seq?: number } = {
        title: title.trim(),
      };

      if (!isEditMode) {
        // 생성 모드: 현재 최대 순서 + 1
        data.seq = maxSeq + 1;
      }

      await onSave(data);
      onClose();
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        '챕터 저장에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h2 className="text-xl font-semibold text-slate-800">{isEditMode ? '챕터 편집' : '챕터 추가'}</h2>
          <button onClick={onClose} className="text-slate-400 transition-colors hover:text-slate-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* 에러 메시지 */}
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* 제목 */}
            <div>
              <label htmlFor="chapter-title" className="mb-2 block text-sm font-medium text-slate-700">
                챕터 제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="chapter-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="챕터 제목을 입력하세요"
              />
            </div>
          </div>

          {/* 버튼 */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg bg-slate-200 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-50">
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
              {isSubmitting ? '저장 중...' : isEditMode ? '저장' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChapterEditModal;
